/**
 * Symbolregister för Centralspec-palett. Varje symbol är en egen ritning —
 * originalverk i SS-EN 60617-stil. Samma uppsättning som enlinje.se/symboler
 * listar, men omtecknade för att undvika upphovsrättsintrång.
 *
 * Alla symboler renderas i ett 48×48 viewBox med stroke-width 2 och ren svart
 * linjegrafik (ingen fyllning utom för fyllda cirklar/trianglar). Storlek
 * anpassas via width/height-prop från användaren.
 */

import type { ReactNode, SVGProps } from "react";

type SvgProps = {
  size?: number;
} & Omit<SVGProps<SVGSVGElement>, "width" | "height" | "viewBox">;

function Svg({ size = 40, children, ...rest }: SvgProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Gemensam topp/botten-anslutningslinje som alla skydd delar. */
function Terminals({ top = true, bottom = true }: { top?: boolean; bottom?: boolean }) {
  return (
    <>
      {top ? <line x1="24" y1="4" x2="24" y2="10" /> : null}
      {bottom ? <line x1="24" y1="38" x2="24" y2="44" /> : null}
    </>
  );
}

// --- MCB — Dvärgbrytare ---
// Brytare ritas som linje med en fjädrande arm som "bryter" fram till en punkt.
export function MCB(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <line x1="24" y1="10" x2="16" y2="20" />
      <line x1="16" y1="20" x2="24" y2="38" />
      <circle cx="24" cy="38" r="1.5" fill="currentColor" />
      <circle cx="24" cy="10" r="1.5" fill="currentColor" />
      <text x="30" y="28" fontSize="9" stroke="none" fill="currentColor">B</text>
    </Svg>
  );
}

// --- MCB 3-pol ---
export function MCB3(props: SvgProps) {
  return (
    <Svg {...props}>
      {[14, 24, 34].map((x) => (
        <g key={x}>
          <line x1={x} y1="4" x2={x} y2="10" />
          <line x1={x} y1="10" x2={x - 3} y2="20" />
          <line x1={x - 3} y1="20" x2={x} y2="38" />
          <line x1={x} y1="38" x2={x} y2="44" />
          <circle cx={x} cy="10" r="1.3" fill="currentColor" />
          <circle cx={x} cy="38" r="1.3" fill="currentColor" />
        </g>
      ))}
      <line x1="14" y1="16" x2="34" y2="16" strokeDasharray="2 2" />
    </Svg>
  );
}

// --- RCD — Jordfelsbrytare ---
// Skiss: vågig linje (transformator) med trippel-arm.
export function RCD(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="10" y="14" width="28" height="20" rx="2" />
      <path d="M14 24 Q17 20, 20 24 T26 24 T32 24" />
      <circle cx="34" cy="20" r="2" />
      <line x1="24" y1="38" x2="24" y2="34" />
    </Svg>
  );
}

// --- RCD 3-pol ---
export function RCD3(props: SvgProps) {
  return (
    <Svg {...props}>
      {[12, 20, 28, 36].map((x) => (
        <g key={x}>
          <line x1={x} y1="4" x2={x} y2="10" />
          <line x1={x} y1="34" x2={x} y2="44" />
        </g>
      ))}
      <rect x="8" y="10" width="32" height="24" rx="2" />
      <path d="M12 22 Q15 18, 18 22 T24 22 T30 22 T36 22" />
      <circle cx="36" cy="28" r="2" />
    </Svg>
  );
}

// --- RCBO — Jordfelsautomat ---
// Kombinerad MCB + RCD i en modul.
export function RCBO(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="10" y="12" width="28" height="24" rx="2" />
      <line x1="24" y1="14" x2="18" y2="22" />
      <line x1="18" y1="22" x2="24" y2="30" />
      <path d="M14 34 Q17 31, 20 34 T26 34" />
      <circle cx="30" cy="32" r="1.5" fill="currentColor" />
    </Svg>
  );
}

// --- RCBO 3-pol ---
export function RCBO3(props: SvgProps) {
  return (
    <Svg {...props}>
      {[14, 24, 34].map((x) => (
        <g key={x}>
          <line x1={x} y1="4" x2={x} y2="10" />
          <line x1={x} y1="38" x2={x} y2="44" />
          <line x1={x} y1="12" x2={x - 2} y2="18" />
          <line x1={x - 2} y1="18" x2={x} y2="26" />
        </g>
      ))}
      <rect x="8" y="10" width="32" height="28" rx="2" />
      <path d="M12 32 Q16 28, 20 32 T28 32 T36 32" />
    </Svg>
  );
}

// --- MCCB / Effektbrytare ---
// Tung bryt-box med tydliga terminaler.
export function MCCB(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="8" y="12" width="32" height="24" rx="1" />
      <line x1="24" y1="14" x2="16" y2="24" />
      <line x1="16" y1="24" x2="24" y2="34" />
      <circle cx="24" cy="14" r="1.8" fill="currentColor" />
      <circle cx="24" cy="34" r="1.8" fill="currentColor" />
      <text x="27" y="26" fontSize="7" stroke="none" fill="currentColor">EB</text>
    </Svg>
  );
}

// --- LBS — Lastbrytare ---
// Som MCB men utan skyddsfunktion, öppen brytare-symbol.
export function LBS(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <line x1="24" y1="10" x2="14" y2="24" />
      <line x1="24" y1="38" x2="24" y2="24" />
      <circle cx="24" cy="10" r="1.5" fill="currentColor" />
      <circle cx="24" cy="38" r="1.5" fill="currentColor" />
    </Svg>
  );
}

// --- SLF — Säkringslastbrytare ---
// Lastbrytare med inbyggd säkring (rektangel).
export function SLF(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <line x1="24" y1="10" x2="14" y2="20" />
      <rect x="20" y="20" width="8" height="18" />
      <circle cx="24" cy="10" r="1.5" fill="currentColor" />
      <circle cx="24" cy="38" r="1.5" fill="currentColor" />
    </Svg>
  );
}

// --- Smältsäkring ---
export function FUSE(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="18" y="10" width="12" height="28" rx="1" />
      <line x1="24" y1="12" x2="24" y2="36" />
    </Svg>
  );
}

// --- SPD / OSP — Överspänningsskydd ---
// Rektangel med pil nedåt (överspänning leds till jord).
export function SPD(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="24" y1="4" x2="24" y2="14" />
      <rect x="14" y="14" width="20" height="18" />
      <path d="M19 20 L29 20 L24 28 Z" fill="currentColor" />
      <line x1="24" y1="32" x2="24" y2="38" />
      <line x1="16" y1="40" x2="32" y2="40" />
      <line x1="19" y1="43" x2="29" y2="43" />
      <line x1="22" y1="46" x2="26" y2="46" />
    </Svg>
  );
}

// --- Kontaktor K ---
// Dubbla kontakter med spole-rektangel.
export function CONTACTOR(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <line x1="24" y1="10" x2="17" y2="19" />
      <line x1="17" y1="19" x2="17" y2="29" />
      <line x1="17" y1="29" x2="24" y2="38" />
      <rect x="28" y="19" width="10" height="10" />
      <line x1="17" y1="24" x2="28" y2="24" strokeDasharray="2 1" />
    </Svg>
  );
}

// --- Hjälprelä HR ---
export function RELAY(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="12" y="14" width="24" height="20" rx="1" />
      <line x1="12" y1="24" x2="20" y2="24" />
      <line x1="20" y1="24" x2="28" y2="20" />
      <line x1="28" y1="24" x2="36" y2="24" />
      <circle cx="28" cy="24" r="1.5" fill="currentColor" />
      <text x="16" y="29" fontSize="6" stroke="none" fill="currentColor">HR</text>
    </Svg>
  );
}

// --- Trappautomat / timer UR ---
export function TIMER(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <circle cx="24" cy="24" r="10" />
      <line x1="24" y1="24" x2="24" y2="17" />
      <line x1="24" y1="24" x2="29" y2="27" />
      <line x1="24" y1="14" x2="24" y2="12" />
      <line x1="34" y1="24" x2="36" y2="24" />
    </Svg>
  );
}

// --- Impulsrelä IR ---
export function IMPULSE(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="12" y="14" width="24" height="20" rx="1" />
      <path d="M16 28 L20 22 L24 28 L28 22 L32 28" />
      <text x="18" y="17" fontSize="5" stroke="none" fill="currentColor">IR</text>
    </Svg>
  );
}

// --- Transformator T ---
// Två överlappande cirklar.
export function TRANSFORMER(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <circle cx="20" cy="24" r="7" />
      <circle cx="28" cy="24" r="7" />
    </Svg>
  );
}

// --- Energimätare KWH ---
export function METER(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="10" y="12" width="28" height="24" rx="1" />
      <text x="14" y="28" fontSize="8" stroke="none" fill="currentColor">kWh</text>
    </Svg>
  );
}

// --- Energimätare med strömtrafo KWHS ---
export function METER_CT(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="10" y="10" width="28" height="22" rx="1" />
      <text x="14" y="24" fontSize="7" stroke="none" fill="currentColor">kWh</text>
      <circle cx="18" cy="38" r="4" />
      <circle cx="30" cy="38" r="4" />
    </Svg>
  );
}

// --- Strömtrafo CT ---
export function CT(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="24" y1="4" x2="24" y2="44" />
      <circle cx="24" cy="24" r="10" />
      <text x="18" y="28" fontSize="6" stroke="none" fill="currentColor">CT</text>
    </Svg>
  );
}

// --- Motorskydd MS ---
export function MOTOR_PROTECTION(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="10" y="12" width="28" height="24" rx="1" />
      <circle cx="24" cy="24" r="7" />
      <text x="20" y="27" fontSize="7" stroke="none" fill="currentColor">M</text>
    </Svg>
  );
}

// --- PUS-plint ---
// Plint med flera anslutningar och en liten "PUS"-etikett.
export function PUS_TERMINAL(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="8" y1="24" x2="40" y2="24" strokeWidth={3} />
      {[12, 20, 28, 36].map((x) => (
        <g key={x}>
          <line x1={x} y1="8" x2={x} y2="24" />
          <circle cx={x} cy="8" r="1.8" fill="currentColor" />
        </g>
      ))}
      <text x="14" y="40" fontSize="7" stroke="none" fill="currentColor">PUS</text>
    </Svg>
  );
}

// --- N-plint / nollskena ---
export function N_TERMINAL(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="6" y1="22" x2="42" y2="22" strokeWidth={3} />
      <line x1="6" y1="26" x2="42" y2="26" strokeWidth={3} />
      {[14, 24, 34].map((x) => (
        <g key={x}>
          <line x1={x} y1="6" x2={x} y2="22" />
          <circle cx={x} cy="6" r="1.8" fill="currentColor" />
        </g>
      ))}
      <text x="20" y="40" fontSize="8" stroke="none" fill="currentColor">N</text>
    </Svg>
  );
}

// --- Sektioneringsplint ---
export function TERMINAL(props: SvgProps) {
  return (
    <Svg {...props}>
      <rect x="14" y="10" width="20" height="28" rx="1" />
      <line x1="24" y1="4" x2="24" y2="10" />
      <line x1="24" y1="38" x2="24" y2="44" />
      <circle cx="24" cy="24" r="2.5" fill="currentColor" />
    </Svg>
  );
}

// --- Jordsymbol PE ---
export function EARTH(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="24" y1="4" x2="24" y2="28" />
      <line x1="10" y1="30" x2="38" y2="30" strokeWidth={3} />
      <line x1="14" y1="36" x2="34" y2="36" strokeWidth={2.5} />
      <line x1="18" y1="42" x2="30" y2="42" strokeWidth={2} />
    </Svg>
  );
}

// --- PEN-ledare ---
export function PEN(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="24" y1="4" x2="24" y2="24" />
      <text x="14" y="20" fontSize="7" stroke="none" fill="currentColor">PEN</text>
      <line x1="10" y1="28" x2="38" y2="28" strokeWidth={2.5} />
      <line x1="14" y1="34" x2="34" y2="34" strokeWidth={2} />
      <line x1="18" y1="40" x2="30" y2="40" strokeWidth={1.5} />
    </Svg>
  );
}

// --- Stigare (uppgående kabel) ---
export function RISER(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="24" y1="44" x2="24" y2="12" />
      <path d="M18 18 L24 8 L30 18" />
      <circle cx="24" cy="44" r="2" fill="currentColor" />
    </Svg>
  );
}

// --- Utgående kabel ---
export function OUTGOING(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="24" y1="4" x2="24" y2="24" />
      <line x1="24" y1="24" x2="40" y2="24" />
      <path d="M34 18 L42 24 L34 30" />
    </Svg>
  );
}

// --- Reservplats (punkt-/streckad ram) ---
export function SPARE(props: SvgProps) {
  return (
    <Svg {...props}>
      <rect x="10" y="10" width="28" height="28" strokeDasharray="3 3" />
      <text x="17" y="28" fontSize="8" stroke="none" fill="currentColor">Res.</text>
    </Svg>
  );
}

// --- Lampa / belysningsgrupp ---
export function LAMP(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <circle cx="24" cy="24" r="10" />
      <line x1="17" y1="17" x2="31" y2="31" />
      <line x1="31" y1="17" x2="17" y2="31" />
    </Svg>
  );
}

// --- Motor M ---
export function MOTOR(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <circle cx="24" cy="24" r="10" />
      <text x="20" y="28" fontSize="10" stroke="none" fill="currentColor">M</text>
    </Svg>
  );
}

// --- Värmepump / värmesystem ---
export function HEAT_PUMP(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <rect x="10" y="12" width="28" height="24" rx="2" />
      <path d="M16 30 Q20 22, 24 30 T32 30" />
      <text x="18" y="20" fontSize="6" stroke="none" fill="currentColor">VP</text>
    </Svg>
  );
}

// --- Undercentral ---
export function SUB_PANEL(props: SvgProps) {
  return (
    <Svg {...props}>
      <rect x="8" y="10" width="32" height="28" />
      <line x1="24" y1="4" x2="24" y2="10" />
      <line x1="24" y1="38" x2="24" y2="44" />
      <line x1="14" y1="16" x2="34" y2="16" />
      <text x="14" y="30" fontSize="6" stroke="none" fill="currentColor">UC</text>
    </Svg>
  );
}

// --- Kabelmärkning ---
export function CABLE_LABEL(props: SvgProps) {
  return (
    <Svg {...props}>
      <line x1="4" y1="24" x2="44" y2="24" />
      <rect x="16" y="14" width="16" height="20" rx="2" />
      <text x="19" y="28" fontSize="7" stroke="none" fill="currentColor">K1</text>
    </Svg>
  );
}

// --- Tidur / astronomiskt ur ---
export function ASTRO_TIMER(props: SvgProps) {
  return (
    <Svg {...props}>
      <Terminals />
      <circle cx="24" cy="24" r="10" />
      <line x1="24" y1="14" x2="24" y2="22" />
      <line x1="24" y1="22" x2="30" y2="22" />
      <circle cx="18" cy="18" r="1.2" fill="currentColor" />
      <circle cx="30" cy="18" r="1.2" fill="currentColor" />
    </Svg>
  );
}

/**
 * Registret för symbolpaletten. Varje post ger kortkod för inmatning,
 * synlig etikett, kategori för gruppering och en SVG-ritning.
 */
export type SymbolEntry = {
  shortCode: string;
  label: string;
  category:
    | "skydd"
    | "last"
    | "matning"
    | "hjalp"
    | "matare"
    | "plint"
    | "forbindelse"
    | "ovrigt";
  icon: (props: SvgProps) => ReactNode;
};

export const SYMBOL_REGISTRY: ReadonlyArray<SymbolEntry> = [
  // Skydd
  { shortCode: "DB", label: "Dvärgbrytare", category: "skydd", icon: MCB },
  { shortCode: "DB3", label: "Dvärgbrytare 3-pol", category: "skydd", icon: MCB3 },
  { shortCode: "JFB", label: "Jordfelsbrytare", category: "skydd", icon: RCD },
  { shortCode: "JFB3", label: "Jordfelsbrytare 3-pol", category: "skydd", icon: RCD3 },
  { shortCode: "JFA", label: "Jordfelsautomat", category: "skydd", icon: RCBO },
  { shortCode: "JFA3", label: "Jordfelsautomat 3-pol", category: "skydd", icon: RCBO3 },
  { shortCode: "EB", label: "Effektbrytare", category: "skydd", icon: MCCB },
  { shortCode: "SLF", label: "Säkringslastbrytare", category: "skydd", icon: SLF },
  { shortCode: "S", label: "Smältsäkring", category: "skydd", icon: FUSE },
  { shortCode: "SPD", label: "Överspänningsskydd", category: "skydd", icon: SPD },
  { shortCode: "MS", label: "Motorskydd", category: "skydd", icon: MOTOR_PROTECTION },

  // Last / brytare
  { shortCode: "LB", label: "Lastbrytare", category: "last", icon: LBS },

  // Hjälpkomponenter
  { shortCode: "K", label: "Kontaktor", category: "hjalp", icon: CONTACTOR },
  { shortCode: "HR", label: "Hjälprelä", category: "hjalp", icon: RELAY },
  { shortCode: "UR", label: "Trappautomat / timer", category: "hjalp", icon: TIMER },
  { shortCode: "IR", label: "Impulsrelä", category: "hjalp", icon: IMPULSE },
  { shortCode: "AU", label: "Astronomiskt ur", category: "hjalp", icon: ASTRO_TIMER },

  // Mätning
  { shortCode: "KWH", label: "Energimätare", category: "matare", icon: METER },
  { shortCode: "KWHS", label: "Mätare + strömtrafo", category: "matare", icon: METER_CT },
  { shortCode: "CT", label: "Strömtrafo", category: "matare", icon: CT },

  // Matning / transform
  { shortCode: "T", label: "Transformator", category: "matning", icon: TRANSFORMER },

  // Plintar
  { shortCode: "PUS", label: "PUS-plint", category: "plint", icon: PUS_TERMINAL },
  { shortCode: "N-PLINT", label: "N-plint", category: "plint", icon: N_TERMINAL },
  { shortCode: "PLINT", label: "Sektioneringsplint", category: "plint", icon: TERMINAL },

  // Förbindelser / jord
  { shortCode: "PE", label: "Jord (PE)", category: "forbindelse", icon: EARTH },
  { shortCode: "PEN", label: "PEN-ledare", category: "forbindelse", icon: PEN },
  { shortCode: "STIG", label: "Stigare", category: "forbindelse", icon: RISER },
  { shortCode: "UTG", label: "Utgående", category: "forbindelse", icon: OUTGOING },
  { shortCode: "KABEL", label: "Kabelmärkning", category: "forbindelse", icon: CABLE_LABEL },

  // Övrigt
  { shortCode: "UC", label: "Undercentral", category: "ovrigt", icon: SUB_PANEL },
  { shortCode: "M", label: "Motor", category: "ovrigt", icon: MOTOR },
  { shortCode: "LAMPA", label: "Belysningsgrupp", category: "ovrigt", icon: LAMP },
  { shortCode: "VP", label: "Värmepump", category: "ovrigt", icon: HEAT_PUMP },
  { shortCode: "RES", label: "Reservplats", category: "ovrigt", icon: SPARE },
];

export const CATEGORY_LABELS: Record<SymbolEntry["category"], string> = {
  skydd: "Skydd (dvärgbrytare, JFB, SPD)",
  last: "Last / brytare",
  hjalp: "Kontaktorer & reläer",
  matare: "Mätning",
  matning: "Matning & transformatorer",
  plint: "Plintar",
  forbindelse: "Förbindelser & jord",
  ovrigt: "Övriga symboler",
};
